<template>
  <div
    v-if="items.length"
    class="collapsed-orders-bar"
    :style="barStyle"
  >
    <div class="collapsed-orders-bar_header">
      <v-icon size="18" color="#ff9100">
        mdi-chevron-double-down
      </v-icon>
      <span class="collapsed-orders-bar_title">Свёрнутые заказы</span>
      <span v-if="hasOverflow" class="collapsed-orders-bar_counter">{{ items.length }}</span>
    </div>

    <button
      v-if="hasOverflow"
      class="collapsed-orders-bar_arrow"
      :disabled="!canScrollLeft"
      @click="scrollByStep(-1)"
    >
      <v-icon size="24" color="#fff">
        mdi-chevron-left
      </v-icon>
    </button>

    <div ref="scroller" class="collapsed-orders-bar_scroller" @scroll="updateScrollState">
      <template v-for="(item, index) in items">
        <div v-if="index > 0" :key="'sep-' + item.basketId" class="collapsed-orders-bar_separator" />
        <div :key="item.basketId" class="collapsed-orders-bar_item">
          <div class="collapsed-orders-bar_chip" @click="openOrder(item)">
            <v-icon size="18" color="#fff">
              {{ iconFor(item) }}
            </v-icon>
            <div class="collapsed-orders-bar_texts">
              <div class="collapsed-orders-bar_order">
                {{ orderTitle(item) }}
              </div>
              <div class="collapsed-orders-bar_details">
                {{ deliveryLabel(item) }}
              </div>
            </div>
          </div>
          <button class="collapsed-orders-bar_remove" @click.stop="askRemove(item)">
            <v-icon size="18" color="#fff">
              mdi-close
            </v-icon>
          </button>
        </div>
      </template>
    </div>

    <button
      v-if="hasOverflow"
      class="collapsed-orders-bar_arrow"
      :disabled="!canScrollRight"
      @click="scrollByStep(1)"
    >
      <v-icon size="24" color="#fff">
        mdi-chevron-right
      </v-icon>
    </button>

    <primary-confirm-dialog
      v-if="removeCandidate"
      :value="true"
      cancel-text="Оставить"
      confirm-text="Удалить"
      confirm-btn-color="#e91e63"
      @confirm="confirmRemove"
      @cancel="removeCandidate = null"
    >
      <template #title>
        Удалить свёрнутый заказ?
      </template>
      <template #subtitle>
        <div class="text-center">
          {{ removeSubtitle }}
        </div>
      </template>
    </primary-confirm-dialog>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'
  import DeliveryType from '@/models/enums/DeliveryType'

  const SCROLL_STEP = 300

  export default {
    name: 'CollapsedOrdersBar',
    components: { PrimaryConfirmDialog },
    data () {
      return {
        removeCandidate: null,
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false,
        contentCenter: null,
        contentMaxWidth: null,
      }
    },
    computed: {
      ...mapState({
        items: (state) => state.collapsedOrders.items,
      }),
      barStyle () {
        if (this.contentCenter === null) {
          return null
        }
        return {
          left: this.contentCenter + 'px',
          maxWidth: this.contentMaxWidth + 'px',
        }
      },
      removeSubtitle () {
        if (!this.removeCandidate) {
          return ''
        }
        const parts = [this.clientLabel(this.removeCandidate)]
        if (this.removeCandidate.deliveryType) {
          parts.push(this.deliveryLabel(this.removeCandidate))
        }
        if (this.removeCandidate.totalPrice) {
          parts.push(this.formatPrice(this.removeCandidate.totalPrice) + ' ₽')
        }
        return parts.join(' · ') + '.'
      },
    },
    watch: {
      items () {
        this.$nextTick(this.updateScrollState)
      },
    },
    mounted () {
      this.recalcPosition()
      window.addEventListener('resize', this.recalcPosition)
      // Ширина контентной части меняется и без resize окна (сворачивание сайдбара)
      const host = this.$el && this.$el.parentElement
      if (host && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.recalcPosition)
        this.resizeObserver.observe(host)
      }
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.recalcPosition)
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }
    },
    methods: {
      // Панель fixed: центр и 80% ширины считаем от контентной части (родителя), не от окна
      recalcPosition () {
        const host = this.$el && this.$el.parentElement
        if (!host) {
          return
        }
        const rect = host.getBoundingClientRect()
        this.contentCenter = Math.round(rect.left + rect.width / 2)
        this.contentMaxWidth = Math.round(rect.width * 0.8)
        this.$nextTick(this.updateScrollState)
      },
      clientLabel (item) {
        return item.clientName || 'Без регистрации'
      },
      orderTitle (item) {
        let title = this.clientLabel(item)
        if (item.totalPrice) {
          title += ' · ' + this.formatPrice(item.totalPrice) + ' ₽'
        }
        return title
      },
      deliveryLabel (item) {
        if (!item.deliveryType) {
          return 'способ и состав не выбраны'
        }
        if (item.deliveryType === DeliveryType.Delivery) {
          return 'Доставка'
        }
        return DeliveryType.getName(item.deliveryType)
      },
      iconFor (item) {
        if (!item.deliveryType) {
          return 'mdi-help-circle-outline'
        }
        if (item.deliveryType === DeliveryType.Self) {
          return 'mdi-storefront-outline'
        }
        return 'mdi-walk'
      },
      formatPrice (value) {
        return Number(value).toLocaleString('ru-RU')
      },
      openOrder (item) {
        this.$router.push({ path: '/new-order', query: { id: String(item.basketId) } })
      },
      askRemove (item) {
        this.removeCandidate = item
      },
      confirmRemove () {
        this.$store.commit('collapsedOrders/remove', this.removeCandidate.basketId)
        this.removeCandidate = null
      },
      scrollByStep (direction) {
        this.$refs.scroller.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' })
      },
      updateScrollState () {
        const scroller = this.$refs.scroller
        if (!scroller) {
          this.hasOverflow = false
          return
        }
        this.hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1
        this.canScrollLeft = scroller.scrollLeft > 1
        this.canScrollRight = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
      },
    },
  }
</script>

<style lang="less" scoped>
.collapsed-orders-bar {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 80vw; // до первого recalcPosition; дальше инлайн-стиль от ширины контентной части
  padding: 12px;
  background: #040404;
  border-radius: 12px;

  &_header {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
  }

  &_title {
    font-size: 15px;
    font-weight: 500;
    color: #fff;
    white-space: nowrap;
  }

  &_counter {
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.15);
    font-size: 12px;
    color: #fff;
  }

  &_scroller {
    display: flex;
    align-items: center;
    gap: 11px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &_separator {
    flex: 0 0 auto;
    width: 1px;
    height: 38px;
    background: rgba(255, 255, 255, 0.35);
  }

  &_item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  &_chip {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 50px;
    padding: 0 14px;
    background: #ff9100;
    border-radius: 10px;
    cursor: pointer;

    &:hover {
      background: darken(#ff9100, 5%);
    }
  }

  &_texts {
    display: flex;
    flex-direction: column;
  }

  &_order {
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: #fff;
    white-space: nowrap;
  }

  &_details {
    font-size: 11px;
    line-height: 13px;
    color: rgba(255, 255, 255, 0.8);
    white-space: nowrap;
  }

  &_remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  &_arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    flex: 0 0 auto;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.15);
    cursor: pointer;

    &:disabled {
      background: rgba(255, 255, 255, 0.05);
      cursor: default;
      opacity: 0.5;
    }
  }
}
</style>
